import React, { Fragment, useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { Dialog, Transition } from '@headlessui/react';
import { ExclamationTriangleIcon, XMarkIcon } from '@heroicons/react/24/outline';
import type { Equipamento } from '../../../../models';
import { ORIGEM_ATIVO_LABELS, TIPO_ATIVO_LABELS } from './ativos.definitions';
import type { ContextoAtivos, PendenciaAtivo } from './ativos.utils';
import AtivoFichaForm from './AtivoFichaForm';
import AtivoSituacaoSecao from './AtivoSituacaoSecao';
import AtivoComentarios from './AtivoComentarios';
import AtivoHistorico from './AtivoHistorico';
import AtivoAnexos from './AtivoAnexos';
import AtivoExclusaoSecao from './AtivoExclusaoSecao';
import AtivoExcluidoFaixa from './AtivoExcluidoFaixa';
import type { FormFichaAtivo } from './ativoFicha.form';
import { normalizarPatrimonio } from '../../../../models/equipamento.utils';

interface AtivoDrawerProps {
  /** 'novo' = cadastro; id = ficha existente; null = fechado. */
  aberto: string | null;
  /** Lista em tempo real (useEquipamentosQuery) — a ficha reflete na hora o que as Cloud
   * Functions de sync escreverem. */
  ativos: Equipamento[];
  /** Excluídos (exclusão lógica) — abrem só pra consulta, com opção de restaurar. */
  excluidos?: Equipamento[];
  /** Valores iniciais do cadastro novo (aberto = 'novo'). */
  inicialNovo?: Partial<FormFichaAtivo>;
  pendenciasPorId: Map<string, PendenciaAtivo[]>;
  contexto: ContextoAtivos;
  onAbrir: (id: string) => void;
  onClose: () => void;
}

/** Ficha do ativo (cadastro/edição) — mesmo formato de modal grande dos drawers do SI. */
const ABAS_FICHA = [
  { valor: 'dados', label: 'Dados' },
  { valor: 'historico', label: 'Histórico' },
  { valor: 'compra', label: 'Compra' },
  { valor: 'anexos', label: 'Anexos' },
] as const;

export default function AtivoDrawer({
  aberto,
  ativos,
  excluidos = [],
  inicialNovo,
  pendenciasPorId,
  contexto,
  onAbrir,
  onClose,
}: AtivoDrawerProps) {
  const [aba, setAba] = useState<(typeof ABAS_FICHA)[number]['valor']>('dados');
  // Toda ficha abre na aba principal (inclusive ao trocar de ativo por "Abrir este cadastro").
  // Ao fechar, `aberto` vira null antes da animação de saída terminar — sem guardar o
  // último valor, a ficha se redesenhava como "Novo ativo" por um instante.
  const ultimoAberto = useRef<string | null>(null);
  if (aberto) ultimoAberto.current = aberto;
  const exibido = aberto ?? ultimoAberto.current;
  useEffect(() => {
    if (aberto) setAba('dados');
  }, [aberto]);
  const equipamento =
    exibido && exibido !== 'novo'
      ? (ativos.find((a) => a.id === exibido) ?? excluidos.find((a) => a.id === exibido) ?? null)
      : null;
  const excluido = Boolean(equipamento?.excluido);
  const pendencias = equipamento ? (pendenciasPorId.get(equipamento.id) ?? []) : [];

  return (
    <Transition show={aberto != null} as={Fragment}>
      <Dialog onClose={onClose} className="relative z-50">
        <Transition.Child
          as={Fragment}
          enter="transition-opacity ease-linear duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="transition-opacity ease-linear duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
        </Transition.Child>

        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Dialog.Panel className="relative flex max-h-[90vh] w-full max-w-3xl flex-col overflow-hidden rounded-lg bg-white shadow-xl">
            <div className="flex flex-shrink-0 items-start justify-between gap-3 border-b border-gray-200 p-4">
              <div>
                <Dialog.Title className="font-montserrat text-lg font-semibold text-gray-900">
                  {equipamento
                    ? (normalizarPatrimonio(equipamento.patrimonio) ?? 'Sem patrimônio')
                    : exibido === 'novo'
                      ? 'Novo ativo'
                      : 'Ativo'}
                </Dialog.Title>
                {equipamento && (
                  <p className="text-sm text-gray-500">
                    {TIPO_ATIVO_LABELS[equipamento.tipo] ?? equipamento.tipo} ·{' '}
                    {equipamento.origem ? ORIGEM_ATIVO_LABELS[equipamento.origem] : 'Cadastro anterior à ficha'}
                  </p>
                )}
              </div>
              <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600" title="Fechar">
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            <div className="flex flex-shrink-0 gap-2 border-b border-gray-200 px-4">
              {/* Cadastro novo ainda não tem histórico; Anexos aparece (arquivos sobem ao cadastrar). */}
              {ABAS_FICHA.filter((item) => item.valor !== 'historico' || equipamento).map((item) => (
                <button
                  key={item.valor}
                  type="button"
                  onClick={() => setAba(item.valor)}
                  className={clsx(
                    'border-b-2 px-3 py-2 text-sm font-medium',
                    aba === item.valor ? 'border-primary-600 text-primary-700' : 'border-transparent text-gray-500 hover:text-gray-700'
                  )}
                >
                  {item.label}
                </button>
              ))}
            </div>

            <div className="flex-1 space-y-4 overflow-y-auto p-4">
              {exibido && exibido !== 'novo' && !equipamento && (
                <p className="text-sm text-gray-500">Carregando...</p>
              )}

              {equipamento && excluido && <AtivoExcluidoFaixa equipamento={equipamento} />}
              {aba === 'dados' && !excluido && pendencias.length > 0 && (
                <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm">
                  <p className="flex items-center gap-1.5 font-semibold text-red-700">
                    <ExclamationTriangleIcon className="h-4 w-4" />
                    Pendências deste cadastro
                  </p>
                  <ul className="mt-1 list-disc space-y-0.5 pl-5 text-amber-900">
                    {pendencias.map((p) => (
                      <li key={p.tipo}>{p.mensagem}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Excluído: tudo só pra consulta (fieldset desabilita campos e botões de dentro). */}
              <fieldset disabled={excluido} className="m-0 min-w-0 space-y-4 border-0 p-0">
              {aba === 'dados' && equipamento && (
                <AtivoSituacaoSecao key={`sit-${equipamento.id}`} equipamento={equipamento} contexto={contexto} />
              )}

              {(exibido === 'novo' || equipamento) && (
                <AtivoFichaForm
                  // key força remontar (resetar o form) ao trocar de ativo, ex. "Abrir este cadastro"
                  key={equipamento?.id ?? 'novo'}
                  equipamento={equipamento}
                  inicial={equipamento ? undefined : inicialNovo}
                  onSalvo={(id) => (exibido === 'novo' ? onAbrir(id) : undefined)}
                  onAbrirExistente={onAbrir}
                  // Histórico não é aba do form — o form fica montado (escondido) pra não
                  // perder o que foi digitado ao trocar de aba.
                  aba={aba === 'compra' ? 'compra' : aba === 'anexos' && !equipamento ? 'anexos' : 'dados'}
                  oculto={aba === 'historico' || (aba === 'anexos' && Boolean(equipamento))}
                />
              )}

              {aba === 'dados' && equipamento && <AtivoComentarios equipamento={equipamento} />}
              </fieldset>
              {aba === 'dados' && equipamento && !excluido && (
                <AtivoExclusaoSecao key={`exc-${equipamento.id}`} equipamento={equipamento} contexto={contexto} onExcluido={onClose} />
              )}
              {aba === 'historico' && equipamento && <AtivoHistorico equipamento={equipamento} contexto={contexto} />}
              {aba === 'anexos' && equipamento && (
                <fieldset disabled={excluido} className="m-0 min-w-0 border-0 p-0">
                  <AtivoAnexos equipamento={equipamento} />
                </fieldset>
              )}
            </div>
          </Dialog.Panel>
        </div>
      </Dialog>
    </Transition>
  );
}
